import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, Clock, Printer } from 'lucide-react';
import Header, { WaIcon, LogoIcon } from '../components/Header';
import Footer from '../components/Footer';
import { trackWhatsAppClick } from '../analytics';
import { fmtCOP, WHATSAPP } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Recibo = ({ order }) => {
  const fecha = order.created_at
    ? new Date(order.created_at).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';
  const numero = (order.id || '').slice(0, 8).toUpperCase();
  return (
    <div className="recibo" data-testid="recibo">
      <div className="recibo-head">
        <div className="recibo-marca"><LogoIcon /> <strong>Finca Pescado</strong></div>
        <div className="recibo-info">
          <span>Recibo de compra</span>
          <strong>N° {numero}</strong>
        </div>
      </div>
      <div className="recibo-meta">
        <div>
          <small>Fecha</small>
          <p>{fecha}</p>
        </div>
        {order.nombre && (
          <div>
            <small>Cliente</small>
            <p>{order.nombre}</p>
          </div>
        )}
        {order.telefono && (
          <div>
            <small>Teléfono</small>
            <p>{order.telefono}</p>
          </div>
        )}
        {(order.direccion || order.ciudad) && (
          <div>
            <small>Entrega</small>
            <p>{[order.direccion, order.ciudad].filter(Boolean).join(', ')}</p>
          </div>
        )}
        <div>
          <small>Estado</small>
          <p className="recibo-pagado">Pagado</p>
        </div>
      </div>
      <table className="recibo-tabla">
        <thead>
          <tr><th>Producto</th><th>Kilos</th><th>Precio/kg</th><th>Subtotal</th></tr>
        </thead>
        <tbody>
          {(order.lineas || []).map((l) => (
            <tr key={l.talla}>
              <td>Cachama eviscerada · {l.talla}</td>
              <td>{l.kg} kg</td>
              <td>{fmtCOP(l.precio_kg)}</td>
              <td>{fmtCOP(l.subtotal)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td colSpan="3">Total kilos</td><td>{order.total_kg} kg</td></tr>
          <tr><td colSpan="3">Subtotal productos</td><td>{fmtCOP(order.subtotal_cop != null ? order.subtotal_cop : order.total_cop)}</td></tr>
          <tr><td colSpan="3">Domicilio · {order.zona_label || 'por coordinar'}</td><td>{order.domicilio_cop > 0 ? fmtCOP(order.domicilio_cop) : 'Gratis'}</td></tr>
          <tr className="recibo-total"><td colSpan="3">Total pagado (COP)</td><td>{fmtCOP(order.total_cop)}</td></tr>
        </tfoot>
      </table>
      <p className="recibo-nota">
        *El peso exacto se confirma al pesar tu pedido en la entrega. El domicilio de tu zona ya está incluido en el total pagado.
      </p>
      <p className="recibo-pie">
        Finca Pescado S.A.S · NIT 901.868.384 · Manatí, Atlántico, Colombia · Registro Sanitario INVIMA RSA-3097-2025<br />
        WhatsApp: 310 684 6758 · Fincapescado811@gmail.com
      </p>
    </div>
  );
};

export const PagoExitoso = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [estado, setEstado] = useState('verificando'); // verificando | pagado | pendiente | expirado | error
  const [monto, setMonto] = useState(null);
  const [order, setOrder] = useState(null);
  const intentos = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setEstado('error');
      return;
    }
    let cancelado = false;

    const cargarOrden = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/by-session/${sessionId}`);
        if (!cancelado) setOrder(data);
      } catch (e) {
        /* el recibo es opcional; la confirmación ya se mostró */
      }
    };

    const poll = async () => {
      if (cancelado) return;
      if (intentos.current >= 8) {
        setEstado('pendiente');
        return;
      }
      intentos.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelado) return;
        if (data.payment_status === 'paid') {
          setMonto(data.amount_total);
          setEstado('pagado');
          cargarOrden();
          return;
        }
        if (data.status === 'expired') {
          setEstado('expirado');
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelado) setEstado('error');
      }
    };
    poll();
    return () => { cancelado = true; };
  }, [sessionId]);

  return (
    <>
      <Header />
      <div className="checkout-page">
        <div className="checkout-card" data-testid="pago-exitoso-card">
          {estado === 'verificando' && (
            <>
              <div className="checkout-icon wait"><Loader2 size={38} className="animate-spin" /></div>
              <h1>Verificando tu pago...</h1>
              <p>Estamos confirmando el pago con Stripe. Esto toma solo unos segundos.</p>
            </>
          )}
          {estado === 'pagado' && (
            <>
              <div className="checkout-icon ok"><CheckCircle2 size={40} /></div>
              <h1>¡Pago recibido!</h1>
              <p>
                Tu pedido de cachama fresca quedó confirmado{monto ? ` por ${fmtCOP(Math.round(monto))} COP` : ''}.
                Te contactaremos por WhatsApp para coordinar la entrega y confirmar el peso exacto.
              </p>
              {order && <Recibo order={order} />}
              <div className="checkout-acciones" style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20 }}>
                {order && (
                  <button type="button" className="btn btn-stripe" style={{ width: 'auto', marginTop: 0 }} onClick={() => window.print()} data-testid="btn-imprimir-recibo">
                    <Printer size={20} /> Descargar / Imprimir recibo
                  </button>
                )}
                <a
                  className="btn btn-wa"
                  href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Hola Finca Pescado, acabo de pagar mi pedido en línea. Quiero coordinar la entrega.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick('pago_exitoso')}
                >
                  <WaIcon /> Coordinar entrega
                </a>
                <Link className="btn btn-stripe" style={{ width: 'auto', marginTop: 0 }} to="/">Volver al inicio</Link>
              </div>
            </>
          )}
          {estado === 'pendiente' && (
            <>
              <div className="checkout-icon wait"><Clock size={38} /></div>
              <h1>Pago en proceso</h1>
              <p>Tu pago está siendo procesado. Si el cobro se realizó, recibirás la confirmación de Stripe por correo. También puedes escribirnos por WhatsApp para confirmar.</p>
              <Link className="btn btn-stripe" style={{ width: 'auto', marginTop: 10 }} to="/">Volver al inicio</Link>
            </>
          )}
          {(estado === 'expirado' || estado === 'error') && (
            <>
              <div className="checkout-icon err"><XCircle size={40} /></div>
              <h1>{estado === 'expirado' ? 'La sesión de pago expiró' : 'No pudimos verificar el pago'}</h1>
              <p>No te preocupes — puedes intentarlo de nuevo o enviarnos tu pedido por WhatsApp.</p>
              <Link className="btn btn-stripe" style={{ width: 'auto', marginTop: 10 }} to="/#pedido">Intentar de nuevo</Link>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export const PagoCancelado = () => (
  <>
    <Header />
    <div className="checkout-page">
      <div className="checkout-card" data-testid="pago-cancelado-card">
        <div className="checkout-icon err"><XCircle size={40} /></div>
        <h1>Pago cancelado</h1>
        <p>No se realizó ningún cobro. Tu pedido sigue disponible — puedes volver a intentarlo o enviarlo por WhatsApp.</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20 }}>
          <Link className="btn btn-stripe" style={{ width: 'auto', marginTop: 0 }} to="/#pedido">Volver a mi pedido</Link>
          <a
            className="btn btn-wa"
            href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Hola Finca Pescado, quiero pedir cachama fresca')}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick('pago_cancelado')}
          >
            <WaIcon /> Pedir por WhatsApp
          </a>
        </div>
      </div>
    </div>
    <Footer />
  </>
);
